import { SalesforceField } from '@/model/SalesforceField';
import { FormEvent, useEffect, useState } from 'react';
import { useFormContext } from 'components/Generic/Forms/SalesforceIntegratedForms/FormContext';

type SortCode = 'sortCode1' | 'sortCode2' | 'sortCode3';

const SalesforceSortCode = ({ fields, stepName }: SalesforceField) => {
  const { updateFormData, updateSummary, formData } = useFormContext();
  const fieldId = fields.salesforceFieldId.value;
  const inputValue = formData.records[0][fieldId] || '';

  const [sortCodes, setSortCodes] = useState<Record<SortCode, { value: string; error: boolean }>>({
    sortCode1: { value: inputValue.split('-')[0] || '', error: false },
    sortCode2: { value: inputValue.split('-')[1] || '', error: false },
    sortCode3: { value: inputValue.split('-')[2] || '', error: false },
  });
  const [errorMessage, setErrorMessage] = useState('');
  const [showError, setShowError] = useState(false);

  const handleChange = (event: FormEvent<HTMLInputElement>, fieldName: SortCode) => {
    setSortCodes((prevState) => ({
      ...prevState,
      [fieldName]: {
        ...prevState[fieldName],
        value: (event.target as HTMLInputElement).value.slice(0, 2),
      }, // each sortCode field max length = 2 e.g 11-22-33
    }));
  };
  const checkError = (event: any, field: SortCode) => {
    switch (true) {
      case event.target.validity.valueMissing:
        setErrorMessage(`Please enter the ${fields.title.value}`);
        setSortCodes((prevState) => ({
          ...prevState,
          [field]: { ...prevState[field], error: true },
        }));
        break;
      case event.target.value.length < 2:
        setErrorMessage(`Please enter the ${fields.title.value}`);
        setSortCodes((prevState) => ({
          ...prevState,
          [field]: { ...prevState[field], error: true },
        }));
        break;
      default:
        setSortCodes((prevState) => ({
          ...prevState,
          [field]: { ...prevState[field], error: false },
        }));
        break;
    }
  };

  useEffect(() => {
    const sortCode = `${sortCodes.sortCode1.value}-${sortCodes.sortCode2.value}-${sortCodes.sortCode3.value}`;
    updateFormData({
      fieldName: fields.salesforceFieldId?.value,
      value: sortCode,
    });
    updateSummary({
      stepName: stepName,
      fieldName: {
        storeName: fields.salesforceFieldId?.value,
        displayName: fields.title.value,
      },
      fieldValue: sortCode,
    });

    if (sortCodes.sortCode1.error || sortCodes.sortCode2.error || sortCodes.sortCode3.error) {
      setShowError(true);
    } else {
      setShowError(false);
    }
  }, [
    fields.salesforceFieldId?.value,
    fields.title.value,
    sortCodes.sortCode1.error,
    sortCodes.sortCode1.value,
    sortCodes.sortCode2.error,
    sortCodes.sortCode2.value,
    sortCodes.sortCode3.error,
    sortCodes.sortCode3.value,
    stepName,
    updateFormData,
    updateSummary,
  ]);

  return (
    <div className="mb-3" style={{ maxWidth: 480 }}>
      <p className="mb-1.5 form-label">
        {fields.title.value} {fields.isRequired.value === true ? '*' : ''}
      </p>
      <section className="sort-code">
        {Object.keys(sortCodes).map((sortCode: SortCode) => (
          <div
            key={sortCode}
            className={`d-flex flex-column form-element ${
              sortCodes[sortCode].error ? 'form-element--error' : ''
            }`}
          >
            <span className="input-box">
              <input
                type="number"
                id={fields.salesforceFieldId.value}
                data-id={fields.salesforceFieldId.value}
                required={fields.isRequired.value}
                className="field-text"
                value={sortCodes[sortCode].value}
                onInput={(event) => handleChange(event, sortCode)}
                onInvalid={(event) => checkError(event, sortCode)}
                onBlur={(event) => checkError(event, sortCode)}
              />
              {sortCodes[sortCode].error && (
                <span className="input-icon input-icon--right input-icon--error">
                  <i className="bi bi-exclamation-triangle" />
                </span>
              )}
            </span>
          </div>
        ))}
      </section>
      {showError && (
        <span className="form-element__error field-error-text text-error-400">{errorMessage}</span>
      )}
    </div>
  );
};

export default SalesforceSortCode;
